import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";

import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import FloatingChat from "@/components/site/FloatingChat";
import WhatsAppButton from "@/components/site/WhatsAppButton";
import MobileActionBar from "@/components/site/MobileActionBar";
import CartDrawer from "@/components/site/CartDrawer";
import { CartProvider } from "@/lib/cart";
import ErrorBoundary from "@/components/ErrorBoundary";

import HomePage from "@/pages/HomePage";
import RepairsPage from "@/pages/RepairsPage";
import AccessoriesPage from "@/pages/AccessoriesPage";
import ZusatzleistungenPage from "@/pages/ZusatzleistungenPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import CheckoutPage from "@/pages/CheckoutPage";
import CheckoutSuccessPage from "@/pages/CheckoutSuccessPage";
import ImpressumPage from "@/pages/legal/ImpressumPage";
import DatenschutzPage from "@/pages/legal/DatenschutzPage";
import AGBPage from "@/pages/legal/AGBPage";

import AdminLogin from "@/pages/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminRepairs from "@/pages/admin/AdminRepairs";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminModels from "@/pages/admin/AdminModels";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminBuyback from "@/pages/admin/AdminBuyback";
import AdminDiscounts from "@/pages/admin/AdminDiscounts";
import AdminContact from "@/pages/admin/AdminContact";
import AdminEmails from "@/pages/admin/AdminEmails";
import AdminRepairCatalog from "@/pages/admin/AdminRepairCatalog";
import BuybackPage from "@/pages/BuybackPage";
import RefurbishedPage from "@/pages/RefurbishedPage";
import OfferPage from "@/pages/OfferPage";
import IpadRepairPage from "@/pages/IpadRepairPage";
import MacbookRepairPage from "@/pages/MacbookRepairPage";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo({ top: 0, behavior: "instant" });
    // Safety net: a dialog/sheet that was open during a route change can leave
    // body styles behind (pointer-events:none / overflow:hidden), which makes
    // the page look frozen or black. Always reset them after navigation.
    document.body.style.pointerEvents = "";
    document.body.style.overflow = "";
    document.body.removeAttribute("data-scroll-locked");
  }, [pathname, hash]);
  return null;
}

function PublicLayout({ children }) {
  return (
    <>
      <Header />
      <main className="pb-24 md:pb-0">{children}</main>
      <Footer />
      <FloatingChat />
      <WhatsAppButton />
      <MobileActionBar />
      <CartDrawer />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
      <CartProvider>
        <BrowserRouter>
          <ScrollToTop />
          <div className="notranslate" translate="no">
            <Toaster
              position="top-center"
              theme="dark"
              toastOptions={{
                style: {
                  background: "#141417",
                  border: "1px solid rgba(255,255,255,0.08)",
                  color: "#fafafa",
                },
              }}
            />
          </div>
          <Routes>
            <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
            <Route path="/reparaturen" element={<PublicLayout><RepairsPage /></PublicLayout>} />
            <Route path="/reparaturen/ipad" element={<PublicLayout><IpadRepairPage /></PublicLayout>} />
            <Route path="/reparaturen/macbook" element={<PublicLayout><MacbookRepairPage /></PublicLayout>} />
            <Route path="/ankauf" element={<PublicLayout><BuybackPage /></PublicLayout>} />
            <Route path="/ankauf/angebot/:id" element={<OfferPage />} />
            <Route path="/refurbished" element={<PublicLayout><RefurbishedPage /></PublicLayout>} />
            <Route path="/zubehoer" element={<PublicLayout><AccessoriesPage /></PublicLayout>} />
            <Route path="/zusatzleistungen" element={<PublicLayout><ZusatzleistungenPage /></PublicLayout>} />
            <Route path="/angebote" element={<Navigate to="/zusatzleistungen" replace />} />
            <Route path="/ueber-uns" element={<PublicLayout><AboutPage /></PublicLayout>} />
            <Route path="/kontakt" element={<PublicLayout><ContactPage /></PublicLayout>} />
            <Route path="/produkt/:sku" element={<PublicLayout><ProductDetailPage /></PublicLayout>} />
            <Route path="/checkout" element={<PublicLayout><CheckoutPage /></PublicLayout>} />
            <Route path="/checkout/erfolg" element={<PublicLayout><CheckoutSuccessPage /></PublicLayout>} />
            <Route path="/impressum" element={<PublicLayout><ImpressumPage /></PublicLayout>} />
            <Route path="/datenschutz" element={<PublicLayout><DatenschutzPage /></PublicLayout>} />
            <Route path="/agb" element={<PublicLayout><AGBPage /></PublicLayout>} />

            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="reparaturen" element={<AdminRepairs />} />
              <Route path="ankauf" element={<AdminBuyback />} />
              <Route path="modelle" element={<AdminModels />} />
              <Route path="produkte" element={<AdminProducts />} />
              <Route path="bestellungen" element={<AdminOrders />} />
              <Route path="kunden" element={<AdminCustomers />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="rabatte" element={<AdminDiscounts />} />
              <Route path="kontakt" element={<AdminContact />} />
              <Route path="emails" element={<AdminEmails />} />
              <Route path="repair-catalog" element={<AdminRepairCatalog />} />
              <Route path="einstellungen" element={<AdminSettings />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </CartProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
